"use client";

import { useState, useEffect, useCallback, useRef } from "react";

interface Testimonial {
  id: number;
  quote: string;
  name: string;
  title: string;
  company: string;
  country: string;
  industry: string;
  initials: string;
  avatarColor: string;
  logoInitials: string;
  rating: number;
  photo: string;
}

const testimonials: Testimonial[] = [
{
  id: 1,
  quote:
  "StanDave LLC transformed our entire supply chain operations. Their strategic consulting reduced our logistics costs by 34% within the first quarter. The team's expertise in global operations is unmatched — we've seen measurable growth across all key metrics.",
  name: "James Whitfield",
  title: "Chief Operations Officer",
  company: "Meridian Logistics Group",
  country: "USA",
  industry: "Logistics",
  initials: "JW",
  avatarColor: "#1A3A5C",
  logoInitials: "MLG",
  rating: 5,
  photo: "https://randomuser.me/api/portraits/men/32.jpg"
},
{
  id: 2,
  quote:
  "The asset management solutions provided by StanDave gave us complete visibility into our inventory across 12 warehouses. Their implementation was seamless and the ROI was evident within weeks. Truly a world-class partner for any manufacturing enterprise.",
  name: "Sophie Harrington",
  title: "VP of Supply Chain",
  company: "Harrington Manufacturing PLC",
  country: "UK",
  industry: "Manufacturing",
  initials: "SH",
  avatarColor: "#2C1A5C",
  logoInitials: "HMP",
  rating: 5,
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_137d1219b-1769835500869.png"
},
{
  id: 3,
  quote:
  "We engaged StanDave for our import/export compliance and global transaction management. Their team navigated complex regulatory requirements with precision. Our cross-border operations are now 40% more efficient and fully compliant with international standards.",
  name: "Liam O'Connor",
  title: "Director of International Trade",
  company: "Pacific Rim Exports Pty Ltd",
  country: "Australia",
  industry: "Import & Export",
  initials: "LO",
  avatarColor: "#1A4A2C",
  logoInitials: "PRE",
  rating: 5,
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e0ae7f32-1767025865140.png"
},
{
  id: 4,
  quote:
  "StanDave's business consulting division helped us restructure our corporate strategy during a critical growth phase. Their data-driven approach and deep industry knowledge delivered a transformation roadmap that exceeded our board's expectations.",
  name: "Klaus Bergmann",
  title: "Chief Executive Officer",
  company: "Bergmann Corporate Solutions GmbH",
  country: "Germany",
  industry: "Corporate",
  initials: "KB",
  avatarColor: "#4A1A1A",
  logoInitials: "BCS",
  rating: 5,
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_16bdee06e-1765181182980.png"
},
{
  id: 5,
  quote:
  "The transportation and logistics optimization StanDave delivered for our retail network was exceptional. Real-time tracking, reduced transit times, and significant cost savings — all delivered on schedule. Their professionalism sets the gold standard in the industry.",
  name: "Amara Johnson",
  title: "Head of Retail Operations",
  company: "NorthStar Retail Ventures",
  country: "USA",
  industry: "Retail & E-Commerce",
  initials: "AJ",
  avatarColor: "#1A3A4A",
  logoInitials: "NSR",
  rating: 5,
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_131c00b1c-1766846862549.png"
},
{
  id: 6,
  quote:
  "Partnering with StanDave for our warehousing and inventory management was the best decision we made this year. Their technology-driven solutions gave us real-time insights and eliminated costly stock discrepancies. Highly recommended for any European enterprise.",
  name: "Isabelle Fontaine",
  title: "Operations Manager",
  company: "Fontaine Logistics SA",
  country: "France",
  industry: "Supply Chain",
  initials: "IF",
  avatarColor: "#3A1A4A",
  logoInitials: "FLS",
  rating: 5,
  photo: "https://randomuser.me/api/portraits/women/26.jpg"
}];


function StarRating({ rating }: {rating: number;}) {
  return (
    <div className="flex items-center gap-1 mb-4">
      {Array.from({ length: 5 }).map((_, i) =>
      <svg
        key={i}
        className={`w-5 h-5 ${
        i < rating ? "text-[#B8860B]" : "text-[#0C1B33]/30"}`
        }
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true">
        
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
    </div>);

}

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const total = testimonials.length;

  const goTo = useCallback(
    (index: number) => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrent((index + total) % total);
      setTimeout(() => setIsAnimating(false), 500);
    },
    [isAnimating, total]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrent((c) => (c + 1) % total);
      }, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, total]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const t = testimonials[current];

  return (
    <section
      className="py-20 bg-[#0C1B33] relative overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}>
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-5"
          style={{
            background:
            "radial-gradient(circle, rgba(184,134,11,0.4) 0%, transparent 70%)"
          }} />
        
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-5"
          style={{
            background:
            "radial-gradient(circle, rgba(184,134,11,0.3) 0%, transparent 70%)"
          }} />
        
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={sectionRef}
          className="text-center mb-14 fade-in-up">
          
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-px w-10 bg-[#B8860B]" />
            <span className="text-[#B8860B] text-sm font-700 uppercase tracking-widest">
              Client Testimonials
            </span>
            <div className="h-px w-10 bg-[#B8860B]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-800 text-white mb-4">
            Trusted by Industry Leaders{" "}
            <span className="text-[#B8860B]">Worldwide</span>
          </h2>
          <p className="text-cream/75 text-lg font-500 max-w-2xl mx-auto">
            Hear from executives and operations leaders across the globe who
            have transformed their businesses with StanDave LLC.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Main Card */}
          <div
            key={current}
            className="mx-auto max-w-4xl"
            style={{ animation: "fadeSlideIn 0.5s ease forwards" }}>
            
            <div
              className="rounded-2xl p-8 sm:p-10 border border-[#B8860B]/30 relative"
              style={{
                background:
                "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(184,134,11,0.04) 100%)",
                backdropFilter: "blur(12px)",
                boxShadow:
                "0 8px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(184,134,11,0.15)"
              }}>
              
              {/* Logo Badge */}
              <div className="absolute top-8 right-8 sm:right-10">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center border-2 border-[#B8860B]/50"
                  style={{
                    background:
                    "linear-gradient(135deg, rgba(184,134,11,0.15) 0%, rgba(184,134,11,0.05) 100%)"
                  }}>
                  
                  <span className="text-[#B8860B] text-xs font-800 tracking-wide">
                    {t.logoInitials}
                  </span>
                </div>
              </div>

              {/* Quote Mark */}
              <div
                className="text-[#B8860B] mb-4"
                style={{ fontSize: "72px", lineHeight: "0.8", fontFamily: "Georgia, serif" }}
                aria-hidden="true">
                
                &ldquo;
              </div>

              {/* Star Rating */}
              <StarRating rating={t.rating} />

              {/* Quote Text */}
              <blockquote className="text-white/90 text-lg sm:text-xl font-500 leading-relaxed mb-8 pr-16">
                {t.quote}
              </blockquote>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-[#B8860B]/40 via-[#B8860B]/20 to-transparent mb-6" />

              {/* Client Info */}
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full flex-shrink-0 border-2 border-[#B8860B]/50 overflow-hidden">
                  <img
                    src={t.photo}
                    alt={`${t.name} profile photo`}
                    className="w-full h-full object-cover" />
                  
                </div>

                {/* Name & Role */}
                <div className="flex-1">
                  <p className="text-white font-700 text-base">{t.name}</p>
                  <p className="text-[#B8860B] text-sm font-600">{t.title}</p>
                  <p className="text-cream/70 text-sm font-500">
                    {t.company} &bull; {t.country}
                  </p>
                </div>

                {/* Industry Tag */}
                <div
                  className="hidden sm:flex items-center px-3 py-1.5 rounded-full border border-[#B8860B]/40"
                  style={{
                    background: "rgba(184,134,11,0.08)"
                  }}>
                  
                  <span className="text-[#B8860B] text-xs font-700 uppercase tracking-wide">
                    {t.industry}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prev}
            aria-label="Previous testimonial"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 w-11 h-11 rounded-full flex items-center justify-center border border-[#B8860B]/50 text-[#B8860B] hover:bg-[#B8860B]/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#B8860B]/50"
            style={{
              background: "rgba(12,27,51,0.9)",
              backdropFilter: "blur(8px)"
            }}>
            
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={next}
            aria-label="Next testimonial"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 w-11 h-11 rounded-full flex items-center justify-center border border-[#B8860B]/50 text-[#B8860B] hover:bg-[#B8860B]/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#B8860B]/50"
            style={{
              background: "rgba(12,27,51,0.9)",
              backdropFilter: "blur(8px)"
            }}>
            
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Dot Indicators */}
        <div className="flex items-center justify-center gap-2.5 mt-8">
          {testimonials.map((_, i) =>
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            className={`transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#B8860B]/50 ${
            i === current ?
            "w-8 h-2.5 bg-[#B8860B]" :
            "w-2.5 h-2.5 bg-[#B8860B]/30 hover:bg-[#B8860B]/60"}`
            } />

          )}
        </div>

        {/* Client Count Strip */}
        <div className="mt-14 flex flex-wrap items-center justify-center gap-8 sm:gap-12">
          {[
          { value: "200+", label: "Global Clients" },
          { value: "98%", label: "Satisfaction Rate" },
          { value: "4.9/5", label: "Average Rating" },
          { value: "15+", label: "Countries Served" }].
          map((stat) =>
          <div key={stat.label} className="text-center">
              <p className="text-[#B8860B] text-2xl font-800">{stat.value}</p>
              <p className="text-cream/70 text-sm font-500 mt-0.5">{stat.label}</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>);

}